/**
 * iter-386u-af — Admin SaaS Monetization Manager (owner only).
 * Compact tabbed UI for Plans, Bundles, Tool Costs. Every change
 * dynamically updates the /billing catalog immediately.
 */
import { useEffect, useState } from 'react';
import { CancellationInsights } from '@/components/billing/CancellationInsights';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2 } from 'lucide-react';
import { API } from '@/App';
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const money = (n) => `\u20B9${(Number(n) || 0).toLocaleString('en-IN')}`;

export default function AdminBillingManager() {
  const [tab, setTab] = useState('plan_center');
  const [plans, setPlans] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [tools, setTools] = useState([]);
  const [addons, setAddons] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const [p, b, t, r, a, tp] = await Promise.all([
        axios.get(`${API}/admin/billing/plans`, { headers }),
        axios.get(`${API}/admin/billing/bundles`, { headers }),
        axios.get(`${API}/admin/billing/tool-costs`, { headers }),
        axios.get(`${API}/admin/billing/revenue`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/marketplace/addons`, { headers }).catch(() => ({ data: { addons: [] } })),
        axios.get(`${API}/admin/template-store`, { headers }).catch(() => ({ data: { templates: [] } })),
      ]);
      setPlans(p.data?.plans || []);
      setBundles(b.data?.bundles || []);
      setTools(t.data?.tool_costs || []);
      setRevenue(r.data || null);
      setAddons(a.data?.addons || []);
      setTemplates(tp.data?.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Access denied — owner only');
    }
  };
  useEffect(() => { load(); }, []);

  const updatePlan = async (id, patch) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/billing/plans/${id}`, patch, { headers });
      toast.success('Plan updated — /billing reflects it instantly');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };
  const updateBundle = async (id, patch) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/billing/bundles/${id}`, patch, { headers });
      toast.success('Bundle updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };
  const updateToolCost = async (tool_key, cost) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/billing/tool-costs`, { tool_key, cost: Number(cost) }, { headers });
      toast.success('Retail credit price updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };
  // iter-386u-bw — Admin-only Wholesale (real per-call cost) field. Used by
  // the "AI Net Profit" widget on this page. Never displayed to end users.
  const updateWholesaleCost = async (tool_key, wholesale_cost) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/billing/tool-costs`, { tool_key, wholesale_cost: Number(wholesale_cost) }, { headers });
      toast.success('Wholesale cost updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };
  const deleteToolCost = async (tool_key, label) => {
    if (!window.confirm(`Soft-delete "${label || tool_key}"? Users on any plan will lose access, but the tool stays recoverable.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/billing/tool-costs/${encodeURIComponent(tool_key)}`, { headers });
      toast.success(`Tool "${label || tool_key}" hidden from Plan Center. Restore any time by re-adding it.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setSaving(false); }
  };
  const upsertAddon = async (patch) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/marketplace/addons`, patch, { headers });
      toast.success('Add-on saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const upsertTemplate = async (patch) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/template-store`, patch, { headers });
      toast.success('Template saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-billing-manager">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">SaaS Monetization Manager</h1>
          <p className="text-stone-500 mt-1">Every edit here updates the public /billing page in real time.</p>
        </div>

        {revenue && (
          <div className="grid md:grid-cols-4 gap-3">
            <StatCard label="MRR" value={money(revenue.mrr)} testid="stat-mrr" />
            <StatCard label="Active Subs" value={revenue.active_subscriptions} testid="stat-subs" />
            <StatCard label="Top-ups (30d)" value={money(revenue.topup_revenue_30d)} testid="stat-topup-30" />
            <StatCard label="Total Top-ups" value={money(revenue.total_topup_all_time)} testid="stat-topup-all" />
          </div>
        )}

        {/* iter-386u-ag — MRR + Top-up chart */}
        <RevenueChart />

        {/* iter-386u-bw — AI Net Profit widget */}
        <NetProfitWidget />
        <CancellationInsights />

        <div className="flex gap-2 border-b border-stone-200">
          {['plan_center', 'bundles', 'tools', 'addons', 'templates', 'commission'].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm capitalize border-b-2 ${
                tab === k ? 'border-emerald-600 text-emerald-700 font-semibold' : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
              data-testid={`billing-tab-${k}`}
            >{k === 'tools' ? 'Tool Costs' : k === 'addons' ? 'Add-ons' : k === 'templates' ? 'Templates' : k === 'plan_center' ? 'Plan Center' : k === 'commission' ? 'Platform Commission' : k}</button>
          ))}
        </div>

        {tab === 'plan_center' && (
          <PlanCenter plans={plans} onSavePlan={updatePlan} onSaved={load} disabled={saving} />
        )}

        {tab === 'commission' && (
          <CommissionLedger />
        )}

        {tab === 'bundles' && (
          <div className="space-y-3" data-testid="bundles-editor">
            {bundles.map((b) => (
              <BundleEditor key={b.id} bundle={b} onSave={(patch) => updateBundle(b.id, patch)} disabled={saving} />
            ))}
          </div>
        )}

        {tab === 'tools' && (
          <div className="space-y-3" data-testid="tools-editor">
            {/* iter-386u-bj — inline "Add New Tool" panel */}
            <NewToolInline onCreated={load} />
            {/* iter-386u-bw — column header for Wholesale vs Retail */}
            <div className="hidden md:flex items-center gap-3 px-3 text-[10px] font-bold uppercase tracking-wider text-stone-500">
              <div className="flex-1">Tool</div>
              <div className="w-24 text-center">Wholesale<br /><span className="font-normal text-stone-400">admin cost</span></div>
              <div className="w-24 text-center">Retail<br /><span className="font-normal text-stone-400">client price</span></div>
              <div className="w-20 text-center">Margin</div>
              <div className="w-8"></div>
            </div>
            {tools.map((t) => {
              const key = t.key || t.tool_key;
              const retail = Number(t.credit_cost ?? t.cost ?? 0);
              const wholesale = Number(t.wholesale_cost ?? 0);
              const margin = retail > 0 ? Math.round(((retail - wholesale) / retail) * 100) : 0;
              return (
                <div key={key} className="flex items-center gap-3 bg-white border border-stone-200 rounded p-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t.label || key}</div>
                    <div className="text-xs text-stone-500 font-mono">{key}</div>
                  </div>
                  <Input
                    type="number"
                    defaultValue={wholesale}
                    className="w-24 text-center"
                    onBlur={(e) => e.target.value !== String(wholesale) && updateWholesaleCost(key, e.target.value)}
                    data-testid={`tool-wholesale-${key}`}
                    title="Admin's real per-call cost (LLM API + infra). Never shown to clients."
                  />
                  <Input
                    type="number"
                    defaultValue={retail}
                    className="w-24 text-center"
                    onBlur={(e) => e.target.value !== String(retail) && updateToolCost(key, e.target.value)}
                    data-testid={`tool-cost-${key}`}
                    title="Credits deducted from a user's balance per call."
                  />
                  <div
                    className={`w-20 text-center text-xs font-bold ${margin >= 40 ? 'text-emerald-700' : margin >= 15 ? 'text-amber-700' : 'text-rose-700'}`}
                    data-testid={`tool-margin-${key}`}
                  >
                    {retail > 0 ? `${margin}%` : '—'}
                  </div>
                  <button
                    onClick={() => deleteToolCost(key, t.label)}
                    className="ml-1 h-8 w-8 flex items-center justify-center rounded text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition"
                    title="Soft-delete tool"
                    data-testid={`tool-delete-${key}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'addons' && (
          <div className="space-y-3" data-testid="addons-editor">
            {addons.map((a) => (
              <AddonEditor key={a.code} addon={a} onSave={upsertAddon} disabled={saving} />
            ))}
            <p className="text-[11px] text-stone-400">Add-ons are billed by deducting credits from the buyer&apos;s balance. 30-day access renews on next subscribe click.</p>
          </div>
        )}

        {tab === 'templates' && (
          <div className="space-y-3" data-testid="templates-editor">
            {templates.map((t) => (
              <TemplateEditor key={t.code} template={t} onSave={upsertTemplate} disabled={saving} />
            ))}
            <p className="text-[11px] text-stone-400">Premium BOQ templates are one-time purchases billed via credits. Grant lifetime access to any user from the Grandfather card on /billing.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-xl font-bold text-stone-900 mt-1">{value}</div>
    </div>
  );
}

function RevenueChart() {
  const [series, setSeries] = useState([]);
  const [range, setRange] = useState(30);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  useEffect(() => {
    axios.get(`${API}/admin/billing/revenue-series?days=${range}`, { headers })
      .then((r) => setSeries(r.data?.series || []))
      .catch(() => setSeries([]));
    // eslint-disable-next-line
  }, [range]);
  if (series.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded p-4 text-xs text-stone-400 text-center" data-testid="revenue-chart-empty">
        No revenue yet — the chart will populate as subscriptions activate and top-ups complete.
      </div>
    );
  }
  const maxV = Math.max(1, ...series.map((s) => Math.max(s.topup, s.mrr_added)));
  const W = 720, H = 160, pad = 24;
  const step = (W - pad * 2) / Math.max(series.length - 1, 1);
  const y = (v) => H - pad - (v / maxV) * (H - pad * 2);
  const topupPath = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${pad + i * step},${y(s.topup)}`).join(' ');
  const mrrPath = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${pad + i * step},${y(s.mrr_added)}`).join(' ');
  const money = (n) => `\u20B9${(Number(n) || 0).toLocaleString('en-IN')}`;
  return (
    <div className="bg-white border border-stone-200 rounded p-4" data-testid="revenue-chart">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-stone-900 text-sm">Revenue trend</h3>
          <div className="flex gap-3 text-[11px] text-stone-500 mt-1">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-sky-500 inline-block"></span>Top-ups</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-600 inline-block"></span>New MRR</span>
          </div>
        </div>
        <select className="text-xs border border-stone-300 rounded px-2 py-1" value={range} onChange={(e) => setRange(Number(e.target.value))} data-testid="revenue-range-select">
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" role="img" aria-label="Revenue chart">
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#e7e5e4" />
        <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#e7e5e4" />
        <path d={topupPath} fill="none" stroke="#0ea5e9" strokeWidth="2" />
        <path d={mrrPath} fill="none" stroke="#059669" strokeWidth="2" />
        {series.map((s, i) => (
          <g key={s.date}>
            <circle cx={pad + i * step} cy={y(s.topup)} r="2.5" fill="#0ea5e9">
              <title>{`${s.date} · Top-up ${money(s.topup)}`}</title>
            </circle>
            <circle cx={pad + i * step} cy={y(s.mrr_added)} r="2.5" fill="#059669">
              <title>{`${s.date} · MRR added ${money(s.mrr_added)}`}</title>
            </circle>
          </g>
        ))}
        <text x={pad} y={pad - 4} className="text-[10px]" fill="#78716c">peak {money(maxV)}</text>
      </svg>
    </div>
  );
}

// iter-386u-bw — AI Net Profit widget (retail − wholesale, per tool + total)
function NetProfitWidget() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  useEffect(() => {
    axios.get(`${API}/admin/billing/net-profit?days=${days}`, { headers })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    // eslint-disable-next-line
  }, [days]);
  const total = data?.total || { calls: 0, retail_credits: 0, wholesale_credits: 0, profit_credits: 0 };
  const rows = data?.per_tool || [];
  const margin = total.retail_credits > 0 ? Math.round((total.profit_credits / total.retail_credits) * 100) : 0;
  return (
    <div className="bg-white border border-stone-200 rounded p-4" data-testid="ai-net-profit-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-stone-900 text-sm">AI Net Profit</h3>
          <div className="text-[11px] text-stone-500 mt-0.5">Retail credits deducted from users − wholesale (real per-call) cost, per tool.</div>
        </div>
        <select className="text-xs border border-stone-300 rounded px-2 py-1" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="net-profit-range-select">
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </div>
      <div className="grid md:grid-cols-4 gap-2 mb-3">
        <div className="bg-stone-50 border border-stone-200 rounded p-2 text-center">
          <div className="text-[10px] uppercase text-stone-500">Calls</div>
          <div className="text-lg font-bold text-stone-900" data-testid="np-calls">{total.calls.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-sky-50 border border-sky-100 rounded p-2 text-center">
          <div className="text-[10px] uppercase text-sky-700">Retail (in)</div>
          <div className="text-lg font-bold text-sky-900" data-testid="np-retail">{total.retail_credits.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-rose-50 border border-rose-100 rounded p-2 text-center">
          <div className="text-[10px] uppercase text-rose-700">Wholesale (out)</div>
          <div className="text-lg font-bold text-rose-900" data-testid="np-wholesale">{total.wholesale_credits.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
          <div className="text-[10px] uppercase text-emerald-700">Net Profit · {margin}%</div>
          <div className="text-lg font-extrabold text-emerald-900" data-testid="np-profit">{total.profit_credits.toLocaleString('en-IN')}</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-stone-400 text-center py-3" data-testid="np-empty">
          No AI tool spend yet in this window — profit rows appear as users invoke tools.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 text-stone-500">
              <tr>
                <th className="text-left px-2 py-1">Tool</th>
                <th className="text-right px-2 py-1">Calls</th>
                <th className="text-right px-2 py-1">Retail</th>
                <th className="text-right px-2 py-1">Wholesale</th>
                <th className="text-right px-2 py-1">Profit</th>
                <th className="text-right px-2 py-1">Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = r.retail_credits > 0 ? Math.round((r.profit_credits / r.retail_credits) * 100) : 0;
                return (
                  <tr key={r.tool_key} className="border-t border-stone-100" data-testid={`np-row-${r.tool_key}`}>
                    <td className="px-2 py-1 font-mono text-[11px] text-stone-700">{r.tool_key}</td>
                    <td className="px-2 py-1 text-right">{r.calls}</td>
                    <td className="px-2 py-1 text-right text-sky-800">{r.retail_credits}</td>
                    <td className="px-2 py-1 text-right text-rose-800">{r.wholesale_credits}</td>
                    <td className="px-2 py-1 text-right font-bold text-emerald-800">{r.profit_credits}</td>
                    <td className={`px-2 py-1 text-right font-bold ${m >= 40 ? 'text-emerald-700' : m >= 15 ? 'text-amber-700' : 'text-rose-700'}`}>{m}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlanEditor({ plan, onSave, disabled }) {
  const [name, setName] = useState(plan.name);
  const [amount, setAmount] = useState(plan.amount || 0);
  const [credits, setCredits] = useState(plan.monthly_credits || 0);
  const [inclusions, setInclusions] = useState((plan.inclusions || []).join('\n'));
  const [isActive, setIsActive] = useState(plan.is_active !== false);
  // iter-386u-bk — incremental pricing tiers
  const [tiersOpen, setTiersOpen] = useState(false);
  const [basePrice, setBasePrice] = useState(plan.base_price ?? plan.amount ?? 0);
  const [baseUsers, setBaseUsers] = useState(plan.base_users ?? 1);
  const [extraRate, setExtraRate] = useState(plan.extra_user_rate ?? 0);
  const [maxProjects, setMaxProjects] = useState(plan.max_projects ?? 0);
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid={`plan-editor-${plan.code}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold">{plan.code}</h3>
          <div className="text-[11px] text-stone-500">rzp: {plan.razorpay_plan_id || '—'}</div>
        </div>
        <label className="text-xs flex items-center gap-2">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid={`plan-active-${plan.code}`} />
          Active
        </label>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase text-stone-500">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-stone-500">Monthly ₹</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-stone-500">Monthly credits</label>
          <Input type="number" value={credits} onChange={(e) => setCredits(Number(e.target.value))} />
        </div>
      </div>

      {/* iter-386u-bk — Edit Tiers: per-plan incremental pricing math */}
      <div className="mt-3 border border-dashed border-emerald-300 rounded bg-emerald-50/30 p-3" data-testid={`plan-tiers-${plan.code}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-emerald-900">Incremental Pricing Tiers</div>
            <div className="text-[10px] text-stone-500">
              Formula on /billing: <code>base + max(0, selected − base_users) × extra_rate</code>
            </div>
          </div>
          <Button size="sm" variant={tiersOpen ? 'outline' : 'default'}
            className={tiersOpen ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            onClick={() => setTiersOpen((v) => !v)}
            data-testid={`plan-tiers-toggle-${plan.code}`}
          >{tiersOpen ? 'Close' : 'Edit Tiers'}</Button>
        </div>
        {tiersOpen && (
          <div className="mt-3 grid md:grid-cols-4 gap-3" data-testid={`plan-tiers-form-${plan.code}`}>
            <div>
              <label className="text-[10px] uppercase text-stone-500">Base Price (₹/mo)</label>
              <Input type="number" value={basePrice} onChange={(e) => setBasePrice(Number(e.target.value))}
                data-testid={`plan-base-price-${plan.code}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-stone-500">Base Users Included</label>
              <Input type="number" min={1} value={baseUsers} onChange={(e) => setBaseUsers(Number(e.target.value))}
                data-testid={`plan-base-users-${plan.code}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-stone-500">₹ / extra user / mo</label>
              <Input type="number" value={extraRate} onChange={(e) => setExtraRate(Number(e.target.value))}
                data-testid={`plan-extra-rate-${plan.code}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase text-stone-500">Max Projects</label>
              <Input type="number" min={0} value={maxProjects} onChange={(e) => setMaxProjects(Number(e.target.value))}
                data-testid={`plan-max-projects-${plan.code}`} />
            </div>
          </div>
        )}
      </div>

      <div className="mt-3">
        <label className="text-[10px] uppercase text-stone-500">Inclusions (one per line)</label>
        <textarea rows={4} className="w-full border border-stone-300 rounded p-2 text-sm mt-1" value={inclusions} onChange={(e) => setInclusions(e.target.value)} data-testid={`plan-inclusions-${plan.code}`} />
      </div>
      <div className="mt-3 text-right">
        <Button size="sm" disabled={disabled} onClick={() => onSave({
          name, amount, monthly_credits: credits,
          inclusions: inclusions.split('\n').map((s) => s.trim()).filter(Boolean),
          is_active: isActive,
          base_price: Number(basePrice),
          base_users: Number(baseUsers),
          extra_user_rate: Number(extraRate),
          max_projects: Number(maxProjects),
        })} data-testid={`plan-save-${plan.code}`}>Save</Button>
      </div>
    </div>
  );
}

function BundleEditor({ bundle, onSave, disabled }) {
  const [name, setName] = useState(bundle.name);
  const [credits, setCredits] = useState(bundle.credits);
  const [amount, setAmount] = useState(bundle.amount);
  const [isActive, setIsActive] = useState(bundle.is_active !== false);
  return (
    <div className="bg-white border border-stone-200 rounded p-4 flex items-center gap-3" data-testid={`bundle-editor-${bundle.code}`}>
      <div className="flex-1">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-2" />
        <div className="text-[11px] text-stone-500">{bundle.code}</div>
      </div>
      <div>
        <label className="text-[10px] uppercase text-stone-500">Credits</label>
        <Input type="number" value={credits} onChange={(e) => setCredits(Number(e.target.value))} className="w-24" />
      </div>
      <div>
        <label className="text-[10px] uppercase text-stone-500">₹</label>
        <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-24" />
      </div>
      <label className="text-xs flex items-center gap-1">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
      </label>
      <Button size="sm" disabled={disabled} onClick={() => onSave({ name, credits, amount, is_active: isActive })} data-testid={`bundle-save-${bundle.code}`}>Save</Button>
    </div>
  );
}

function AddonEditor({ addon, onSave, disabled }) {
  const [name, setName] = useState(addon.name || '');
  const [description, setDescription] = useState(addon.description || '');
  const [creditsCost, setCreditsCost] = useState(addon.credits_cost || addon.amount || 0);
  const [isActive, setIsActive] = useState(addon.is_active !== false);
  return (
    <div className="bg-white border border-stone-200 rounded p-4" data-testid={`addon-editor-${addon.code}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-2" />
          <div className="text-[11px] text-stone-500">{addon.code}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase text-stone-500">Credits/month</label>
          <Input type="number" value={creditsCost} onChange={(e) => setCreditsCost(Number(e.target.value))} className="w-28" />
        </div>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
        </label>
        <Button size="sm" disabled={disabled} onClick={() => onSave({ code: addon.code, name, description, credits_cost: creditsCost, amount: creditsCost, is_active: isActive })} data-testid={`addon-save-${addon.code}`}>Save</Button>
      </div>
      <textarea rows={2} className="w-full border border-stone-300 rounded p-2 text-sm mt-3" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Public description shown on /billing" />
    </div>
  );
}

function TemplateEditor({ template, onSave, disabled }) {
  const [name, setName] = useState(template.name || '');
  const [description, setDescription] = useState(template.description || '');
  const [creditsCost, setCreditsCost] = useState(template.credits_cost || template.amount || 0);
  const [mainCategory, setMainCategory] = useState(template.main_category || '');
  const [isActive, setIsActive] = useState(template.is_active !== false);
  return (
    <div className="bg-white border border-stone-200 rounded p-4" data-testid={`template-editor-${template.code}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-2" />
          <div className="text-[11px] text-stone-500">{template.code}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase text-stone-500">Category</label>
          <Input value={mainCategory} onChange={(e) => setMainCategory(e.target.value)} className="w-28" placeholder="kitchen" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-stone-500">Credits · once</label>
          <Input type="number" value={creditsCost} onChange={(e) => setCreditsCost(Number(e.target.value))} className="w-28" />
        </div>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
        </label>
        <Button size="sm" disabled={disabled} onClick={() => onSave({ code: template.code, name, description, main_category: mainCategory, credits_cost: creditsCost, amount: creditsCost, is_active: isActive })} data-testid={`template-save-${template.code}`}>Save</Button>
      </div>
      <textarea rows={2} className="w-full border border-stone-300 rounded p-2 text-sm mt-3" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Public description shown on /billing" />
    </div>
  );
}


// iter-386u-bj — Inline "Add New Tool" form on the Tool Costs tab.
function NewToolInline({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [cost, setCost] = useState(10);
  const [wholesale, setWholesale] = useState(0);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const save = async () => {
    if (!key.trim()) { toast.error('Enter a technical key'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/billing/tool-costs/new`,
        { key: key.trim(), label: label.trim() || key.trim(), credit_cost: Number(cost) || 0, wholesale_cost: Number(wholesale) || 0 },
        { headers });
      toast.success(`Tool "${label || key}" registered. Auto-appears in Plan Center → AI Tools.`);
      setOpen(false); setKey(''); setLabel(''); setCost(10); setWholesale(0);
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-dashed border-emerald-300 rounded bg-emerald-50/40 p-3" data-testid="tool-cost-new-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-emerald-900">Register a custom AI tool</div>
          <div className="text-[10px] text-stone-500">New tools appear instantly in Plan Center → AI Tools so you can gate them per plan.</div>
        </div>
        <Button size="sm" variant={open ? 'outline' : 'default'} onClick={() => setOpen((v) => !v)}
          className={open ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
          data-testid="tool-cost-new-toggle"
        >{open ? 'Close' : '+ Add New Tool'}</Button>
      </div>
      {open && (
        <div className="mt-3 grid md:grid-cols-5 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase text-stone-500">Technical key</label>
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="advanced_pdf" data-testid="tool-cost-new-key" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-stone-500">Human label</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Advanced PDF" data-testid="tool-cost-new-label" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-stone-500" title="Admin's real per-call cost">Wholesale</label>
            <Input type="number" value={wholesale} onChange={(e) => setWholesale(e.target.value)} data-testid="tool-cost-new-wholesale" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-stone-500" title="Credits charged to the user">Retail</label>
            <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="tool-cost-new-cost" />
          </div>
          <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tool-cost-new-save">
            {busy ? 'Saving…' : 'Register tool'}
          </Button>
        </div>
      )}
    </div>
  );
}


// iter-386u-aw — Platform Commission Ledger view (5% skim from Enterprise sub-tenants)
function CommissionLedger() {
  const [state, setState] = useState({ entries: [], total_gross_paise: 0, total_commission_paise: 0, count: 0, loaded: false });
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  useEffect(() => {
    axios.get(`${API}/admin/platform-commission-ledger`, { headers })
      .then((r) => setState({ ...r.data, loaded: true }))
      .catch((e) => { toast.error(e.response?.data?.detail || 'Failed to load'); setState((s) => ({ ...s, loaded: true })); });
  }, []);
  const money = (paise) => `\u20B9${((Number(paise) || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const dt = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };

  const downloadCsv = () => {
    const rows = state.entries || [];
    const header = ['Created At', 'Source', 'Sub-tenant', 'Sub-tenant User ID', 'Source ID', 'Gross (INR)', 'Commission (INR)'];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = rows.map((e) => [
      e.created_at || '',
      e.source || '',
      e.sub_tenant_name || '',
      e.sub_tenant_user_id || '',
      e.source_id || '',
      ((Number(e.gross_paise) || 0) / 100).toFixed(2),
      ((Number(e.commission_paise) || 0) / 100).toFixed(2),
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-commission-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length !== 1 ? 's' : ''}`);
  };

  return (
    <div className="space-y-4" data-testid="commission-ledger">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="grid md:grid-cols-3 gap-3 flex-1">
          <StatCard label="Total Entries" value={state.count} testid="commission-count" />
          <StatCard label="Gross Vendor Payouts" value={money(state.total_gross_paise)} testid="commission-gross" />
          <StatCard label="Your 5% Commission" value={money(state.total_commission_paise)} testid="commission-total" />
        </div>
        <Button size="sm" variant="outline" onClick={downloadCsv} disabled={!state.entries?.length} data-testid="commission-export-csv">
          Download CSV
        </Button>
      </div>
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="bg-stone-50 px-4 py-2 text-[11px] uppercase tracking-wider text-stone-500 font-semibold border-b border-stone-200">
          Recent commission entries
        </div>
        {!state.loaded && (
          <div className="p-6 text-center text-xs text-stone-400">Loading…</div>
        )}
        {state.loaded && state.entries.length === 0 && (
          <div className="p-8 text-center text-sm text-stone-500" data-testid="commission-empty">
            No commission collected yet. The 5% skim engages the moment an Enterprise sub-tenant with <code>charge_own_vendors=true</code> initiates a vendor payout.
          </div>
        )}
        {state.entries.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50/50 text-[10px] uppercase tracking-wider text-stone-500">
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-left px-4 py-2">Sub-tenant</th>
                <th className="text-right px-4 py-2">Gross</th>
                <th className="text-right px-4 py-2">Commission</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {state.entries.map((e) => (
                <tr key={e.id} className="hover:bg-emerald-50/40" data-testid={`commission-row-${e.id}`}>
                  <td className="px-4 py-2 text-stone-600">{dt(e.created_at)}</td>
                  <td className="px-4 py-2"><span className="text-[10px] uppercase text-stone-400 font-mono">{e.source}</span></td>
                  <td className="px-4 py-2 text-stone-700">{e.sub_tenant_name}</td>
                  <td className="px-4 py-2 text-right text-stone-800 font-medium">{money(e.gross_paise)}</td>
                  <td className="px-4 py-2 text-right text-emerald-700 font-semibold">{money(e.commission_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-stone-500">
        Commission is auto-skimmed on <code>/admin/payouts/vendor</code> when the initiator is on the Enterprise plan and the org has <code>charge_own_vendors=true</code>. Rate defaults to 5% and is configurable via <code>organization_settings.platform_transaction_fee_percentage</code>.
      </p>
    </div>
  );
}


// iter-386u-av — Unified Plan Center: merges plan basics editor + Pivot
// permissions builder + Add-New-Capability into a single owner-friendly pane.
function PlanCenter({ plans, onSavePlan, onSaved, disabled }) {
  const [sections, setSections] = useState({});
  const [activePlanCode, setActivePlanCode] = useState(plans[0]?.code || 'free');
  const [selected, setSelected] = useState(new Set());
  const [savingPerms, setSavingPerms] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newSection, setNewSection] = useState('Custom');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadRegistry = () => {
    axios.get(`${API}/admin/permission-keys`, { headers })
      .then((r) => setSections(r.data?.sections || {}))
      .catch(() => {});
  };
  useEffect(() => { loadRegistry(); }, []);

  useEffect(() => {
    const plan = plans.find((p) => p.code === activePlanCode);
    setSelected(new Set(plan?.permissions || []));
  }, [activePlanCode, plans]);

  const activePlan = plans.find((p) => p.code === activePlanCode);
  const allKeys = Object.entries(sections);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const savePermissions = async () => {
    if (!activePlan?.id) return;
    setSavingPerms(true);
    try {
      await axios.patch(`${API}/admin/billing/plans/${activePlan.id}/permissions`,
        { permissions: [...selected] }, { headers });
      toast.success(`${activePlan.name}: ${selected.size} permission${selected.size !== 1 ? 's' : ''} saved. Users on this plan see the change instantly.`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingPerms(false); }
  };

  const addCapability = async () => {
    const key = newKey.trim();
    const label = newLabel.trim() || key;
    if (!key) { toast.error('Enter a key like can_export_dxf'); return; }
    try {
      await axios.post(`${API}/admin/feature-registry`,
        { key, label, section: newSection || 'Custom' }, { headers });
      toast.success(`Capability "${label}" added to registry.`);
      setNewKey(''); setNewLabel(''); setAddOpen(false);
      loadRegistry();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };

  if (!activePlan) return null;

  return (
    <div className="space-y-4" data-testid="plan-center">
      {/* Plan picker */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Editing plan</div>
            <select value={activePlanCode} onChange={(e) => setActivePlanCode(e.target.value)}
              className="h-10 border border-stone-300 rounded px-3 text-sm bg-white font-semibold"
              data-testid="plan-center-picker">
              {plans.map((p) => <option key={p.code} value={p.code}>{p.name} — {p.code}</option>)}
            </select>
          </div>
          <div className="text-[11px] text-stone-500 flex-1">
            rzp: <span className="font-mono">{activePlan.razorpay_plan_id || '—'}</span> · monthly ₹{Number(activePlan.amount || 0).toLocaleString('en-IN')} · {activePlan.monthly_credits || 0} credits
          </div>
        </div>
      </div>

      {/* Basic plan details */}
      <PlanEditor key={activePlan.id} plan={activePlan} onSave={(patch) => onSavePlan(activePlan.id, patch)} disabled={disabled} />

      {/* Pivot permissions builder */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h3 className="font-semibold text-stone-900">Feature inclusions</h3>
            <p className="text-[11px] text-stone-500 mt-0.5">Tick a capability on the left to grant it to {activePlan.name}. The sidebar, PDF exports and AI buttons all read from this list.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setAddOpen((v) => !v)} data-testid="add-capability-btn">
              {addOpen ? 'Close' : '+ Add New Capability'}
            </Button>
            <Button size="sm" disabled={savingPerms} onClick={savePermissions} data-testid="plan-center-save-btn">
              {savingPerms ? 'Saving…' : `Save ${selected.size} inclusion${selected.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>

        {addOpen && (
          <div className="mb-4 p-3 border border-dashed border-emerald-300 rounded bg-emerald-50/40 grid md:grid-cols-4 gap-2" data-testid="add-capability-form">
            <div>
              <label className="text-[10px] uppercase text-stone-500">Technical key</label>
              <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="can_export_dxf" data-testid="add-capability-key" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-stone-500">Human label</label>
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Export DXF for CNC" data-testid="add-capability-label" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-stone-500">Section</label>
              <Input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="Studio Tools" data-testid="add-capability-section" />
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={addCapability} className="w-full" data-testid="add-capability-save">Add</Button>
            </div>
          </div>
        )}

        <DndContext
          sensors={sensors}
          onDragEnd={({ active, over }) => {
            if (!over || !active) return;
            const key = String(active.id).replace(/^(avail|incl)-/, '');
            const inAvailable = active.id.toString().startsWith('avail-');
            const dropTarget = over.id; // 'available' | 'inclusions'
            setSelected((prev) => {
              const next = new Set(prev);
              if (dropTarget === 'inclusions' && inAvailable) next.add(key);
              else if (dropTarget === 'available' && !inAvailable) next.delete(key);
              return next;
            });
          }}
        >
          <div className="grid md:grid-cols-2 gap-4">
            <PivotColumn
              id="available"
              title="Available"
              badgeClass="bg-stone-50 text-stone-500 border-stone-200"
              containerClass="border-stone-200"
              headerClass="bg-stone-50"
              emptyText="Registry is empty. Click + Add New Capability to register your first feature."
            >
              {allKeys.map(([section, items]) => {
                const rest = items.filter((it) => !selected.has(it.key));
                if (!rest.length) return null;
                return (
                  <div key={section}>
                    <div className="px-3 py-1.5 bg-stone-50/50 text-[10px] uppercase text-stone-400 font-semibold">{section}</div>
                    {rest.map((it) => (
                      <DraggableRow key={it.key} id={`avail-${it.key}`} onClick={() => toggle(it.key)} tone="avail" testid={`perm-avail-${it.key}`}>
                        <span>{it.label}<span className="ml-2 text-[10px] text-stone-400 font-mono">{it.key}</span></span>
                        <span className="text-[10px] text-emerald-600">Add →</span>
                      </DraggableRow>
                    ))}
                  </div>
                );
              })}
              {allKeys.length === 0 && (
                <div className="p-6 text-center text-xs text-stone-400">Registry is empty. Click "+ Add New Capability" to register your first feature.</div>
              )}
            </PivotColumn>
            <PivotColumn
              id="inclusions"
              title={`Inclusions (${selected.size})`}
              badgeClass="bg-emerald-50 text-emerald-800 border-emerald-200"
              containerClass="border-emerald-300"
              headerClass="bg-emerald-50"
              emptyText="Drag features from the left — or click them — to add them here."
            >
              {selected.size === 0 && (
                <div className="p-6 text-center text-xs text-stone-400">Drag features from the left — or click them — to add them here. Users on this plan see them unlock instantly.</div>
              )}
              {allKeys.map(([section, items]) => {
                const chosen = items.filter((it) => selected.has(it.key));
                if (!chosen.length) return null;
                return (
                  <div key={section}>
                    <div className="px-3 py-1.5 bg-emerald-50/40 text-[10px] uppercase text-emerald-700 font-semibold">{section}</div>
                    {chosen.map((it) => (
                      <DraggableRow key={it.key} id={`incl-${it.key}`} onClick={() => toggle(it.key)} tone="incl" testid={`perm-incl-${it.key}`}>
                        <span>{it.label}<span className="ml-2 text-[10px] text-stone-400 font-mono">{it.key}</span></span>
                        <span className="text-[10px] text-rose-600">← Remove</span>
                      </DraggableRow>
                    ))}
                  </div>
                );
              })}
            </PivotColumn>
          </div>
        </DndContext>
        <p className="mt-3 text-[11px] text-stone-500">Rules: drag or click any capability across columns. Unchecking a feature hides its sidebar tab / button for every user on this plan. Owner accounts always see everything.</p>
      </div>
    </div>
  );
}


// iter-386u-ay — DnD helpers for the Pivot builder
function PivotColumn({ id, title, containerClass, headerClass, children }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`border rounded-lg overflow-hidden transition-colors ${containerClass} ${isOver ? 'ring-2 ring-emerald-400' : ''}`} data-testid={`pivot-column-${id}`}>
      <div className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold border-b ${headerClass}`}>
        {title}
      </div>
      <div className="max-h-[520px] overflow-y-auto divide-y divide-stone-100">
        {children}
      </div>
    </div>
  );
}

function DraggableRow({ id, onClick, tone, testid, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  const hoverClass = tone === 'avail' ? 'hover:bg-emerald-50' : 'hover:bg-rose-50';
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // dnd-kit fires clicks even after drag ends; guard.
        if (isDragging) return;
        onClick && onClick(e);
      }}
      className={`w-full text-left px-3 py-2 select-none flex items-center justify-between text-sm text-stone-700 ${hoverClass}`}
      data-testid={testid}
      role="button"
      tabIndex={0}
    >
      {children}
    </div>
  );
}


// Legacy PermissionsPivot kept for backward compat (unused after v-av)
function PermissionsPivot({ plans, onSaved }) {
  const [sections, setSections] = useState({});
  const [activePlanCode, setActivePlanCode] = useState(plans[0]?.code || 'free');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/admin/permission-keys`, { headers })
      .then((r) => setSections(r.data?.sections || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const plan = plans.find((p) => p.code === activePlanCode);
    setSelected(new Set(plan?.permissions || []));
  }, [activePlanCode, plans]);

  const activePlan = plans.find((p) => p.code === activePlanCode);
  const allKeys = Object.entries(sections);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!activePlan?.id) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/billing/plans/${activePlan.id}/permissions`,
        { permissions: [...selected] }, { headers });
      toast.success(`${activePlan.name}: ${selected.size} permission${selected.size !== 1 ? 's' : ''} saved. Users on this plan see the change instantly.`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="permissions-pivot">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Editing plan</div>
          <select value={activePlanCode} onChange={(e) => setActivePlanCode(e.target.value)}
            className="h-9 border border-stone-300 rounded px-2 text-sm bg-white font-semibold"
            data-testid="permissions-plan-picker">
            {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </div>
        <Button size="sm" disabled={saving} onClick={save} data-testid="permissions-save-btn">
          {saving ? 'Saving…' : `Save ${selected.size} permission${selected.size !== 1 ? 's' : ''}`}
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-stone-200 rounded-lg overflow-hidden">
          <div className="bg-stone-50 px-3 py-2 text-[11px] uppercase tracking-wider text-stone-500 font-semibold border-b border-stone-200">Available</div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-stone-100">
            {allKeys.map(([section, items]) => {
              const rest = items.filter((it) => !selected.has(it.key));
              if (!rest.length) return null;
              return (
                <div key={section}>
                  <div className="px-3 py-1.5 bg-stone-50/50 text-[10px] uppercase text-stone-400 font-semibold">{section}</div>
                  {rest.map((it) => (
                    <button key={it.key} onClick={() => toggle(it.key)}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between text-sm text-stone-700"
                      data-testid={`perm-avail-${it.key}`}>
                      <span>{it.label}</span>
                      <span className="text-[10px] text-emerald-600">Add →</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
        <div className="border border-emerald-300 rounded-lg overflow-hidden">
          <div className="bg-emerald-50 px-3 py-2 text-[11px] uppercase tracking-wider text-emerald-800 font-semibold border-b border-emerald-200">Inclusions ({selected.size})</div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-stone-100">
            {selected.size === 0 && (
              <div className="p-6 text-center text-xs text-stone-400">Click features on the left to add them. Users on this plan see them unlock instantly.</div>
            )}
            {allKeys.map(([section, items]) => {
              const chosen = items.filter((it) => selected.has(it.key));
              if (!chosen.length) return null;
              return (
                <div key={section}>
                  <div className="px-3 py-1.5 bg-emerald-50/40 text-[10px] uppercase text-emerald-700 font-semibold">{section}</div>
                  {chosen.map((it) => (
                    <button key={it.key} onClick={() => toggle(it.key)}
                      className="w-full text-left px-3 py-2 hover:bg-rose-50 flex items-center justify-between text-sm text-stone-700"
                      data-testid={`perm-incl-${it.key}`}>
                      <span>{it.label}</span>
                      <span className="text-[10px] text-rose-600">← Remove</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-stone-500">Rules: unchecking a feature hides its button for every user on this plan — the FeatureLocked tooltip fires on hover so they know to upgrade.</p>
    </div>
  );
}

